import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Users, Search, Eye, KeyRound, Clock, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EntityPageHeader } from '@/shared/components'
import { formateursAdminApi } from '../api/formateurs-admin.api'
import type { FormateurDto } from '../api/formateurs-admin.api'

function getInitials(prenom: string, nom: string) {
  return `${prenom?.[0] ?? ''}${nom?.[0] ?? ''}`.toUpperCase()
}

type StatusFilter = 'all' | 'active' | 'inactive'

export function FormateursListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const { data: formateurs = [], isLoading } = useQuery({
    queryKey: ['admin', 'formateurs'],
    queryFn: formateursAdminApi.getAll,
  })

  const filtered = useMemo(() => {
    let result = formateurs
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        (f) =>
          f.prenom.toLowerCase().includes(q) ||
          f.nom.toLowerCase().includes(q) ||
          f.email.toLowerCase().includes(q),
      )
    }
    // Status filtering placeholder - for now all formateurs are considered active
    return result
  }, [formateurs, search])

  const statusFilters: { value: StatusFilter; label: string; count: number }[] = [
    { value: 'all', label: t('admin.formateurs.filterAll', 'Tous'), count: formateurs.length },
    { value: 'active', label: t('admin.formateurs.filterActive', 'Actifs'), count: formateurs.length },
    { value: 'inactive', label: t('admin.formateurs.filterInactive', 'Inactifs'), count: 0 },
  ]

  return (
    <div className="space-y-6">
      <EntityPageHeader
        icon={Users}
        title={t('admin.formateurs.title', 'Formateurs')}
        subtitle={t('admin.formateurs.subtitle', 'Gestion des comptes formateurs')}
        createTo="/formateurs/create"
        createLabel={t('admin.formateurs.create', 'Nouveau formateur')}
      />

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="flex rounded-lg border border-neutral-200 bg-white p-1">
          {statusFilters.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-all',
                statusFilter === f.value
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-neutral-600 hover:bg-neutral-50',
              )}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.formateurs.searchPlaceholder', 'Rechercher par nom, email...')}
            className="h-9 w-full rounded-lg border border-neutral-200 bg-white pl-9 pr-3 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-neutral-100" />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200 bg-gradient-to-r from-primary/5 to-transparent">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-400">
                  {t('admin.formateurs.colFormateur', 'Formateur')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-400">
                  {t('admin.formateurs.colEmail', 'Email')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-400">
                  Structures
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-neutral-400">
                  {t('admin.formateurs.colStatus', 'Statut')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-400">
                  {t('admin.formateurs.colActions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-neutral-400">
                    {t('admin.formateurs.noFormateurs', 'Aucun formateur trouvé')}
                  </td>
                </tr>
              ) : (
                filtered.map((f) => (
                  <FormateurRow key={f.id} formateur={f} navigate={navigate} />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function FormateurRow({
  formateur: f,
  navigate,
}: {
  formateur: FormateurDto
  navigate: ReturnType<typeof useNavigate>
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [showConfirm, setShowConfirm] = useState(false)

  const resetPassword = useMutation({
    mutationFn: () => formateursAdminApi.resetPassword(f.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'formateurs'] })
      setShowConfirm(false)
    },
  })

  return (
    <>
      <tr
        className="cursor-pointer transition-colors hover:bg-neutral-50"
        onClick={() => navigate(`/formateurs/${f.id}`)}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60 text-xs font-bold text-white">
              {getInitials(f.prenom, f.nom)}
            </div>
            <span className="text-sm font-medium text-neutral-900">
              {f.prenom} {f.nom}
            </span>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-neutral-600">{f.email}</td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {f.structureNoms && f.structureNoms.length > 0 ? (
              f.structureNoms.map((nom, i) => (
                <span key={i} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                  {nom}
                </span>
              ))
            ) : (
              <span className="text-xs text-neutral-400">—</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
            Actif
          </span>
        </td>
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-1">
            <button
              onClick={() => navigate(`/formateurs/${f.id}`)}
              className="rounded-lg p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
              title={t('admin.formateurs.view', 'Voir')}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowConfirm(true)}
              className="rounded-lg p-2 text-neutral-400 hover:bg-amber-50 hover:text-amber-600"
              title={t('admin.formateurs.resetPassword', 'Réinitialiser le mot de passe')}
            >
              <KeyRound className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>

      {/* Reset password confirmation */}
      {showConfirm && (
        <tr>
          <td colSpan={5} className="px-4 py-3 bg-amber-50 border-b border-amber-100">
            <div className="flex items-center justify-between">
              <p className="text-sm text-amber-800">
                {t('admin.formateurs.resetConfirm', 'Réinitialiser le mot de passe de {{name}} ? Un nouveau mot de passe sera généré et envoyé par email.', { name: `${f.prenom} ${f.nom}` })}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={resetPassword.isPending}
                  className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
                >
                  {t('common.cancel', 'Annuler')}
                </button>
                <button
                  onClick={() => resetPassword.mutate()}
                  disabled={resetPassword.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {resetPassword.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                  {t('admin.formateurs.resetConfirmButton', 'Réinitialiser')}
                </button>
              </div>
            </div>
            {resetPassword.isError && (
              <p className="mt-2 text-xs text-red-600">
                {t('admin.formateurs.resetError', 'Erreur lors de la réinitialisation')}
              </p>
            )}
            {resetPassword.isSuccess && (
              <p className="mt-2 text-xs text-emerald-600">
                {t('admin.formateurs.resetSuccess', 'Mot de passe réinitialisé et envoyé par email')}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  )
}
